import React, { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
	DialogActions,
  IconButton,
  Typography,
  TextField,
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  Chip,
	Stack,
	InputAdornment,
	Divider
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import { LocalOffer as LocalOfferIcon, Search as SearchIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material'
import Banner from './Banner'
import VariantsDisplay from './VariantsDisplay'

export default function ProductModal({ open, onClose, products, onSave }) {
	const theme = useTheme();
  const searchInputRef = useRef(null)
  const [search, setSearch] = useState('')
  const [visibleCount, setVisibleCount] = useState(8) // mostrar los primeros 8 productos
	const [selectedProducts, setSelectedProducts] = useState([])

	const focusSearchInput = () => {
		setTimeout(() => {
			if (searchInputRef.current) {
				searchInputRef.current.focus()
				searchInputRef.current.select?.()
			}
		}, 0)
	}

	useEffect(() => {
		if (open) {
			focusSearchInput()
		}
	}, [open])

	const normalizedSearch = search.trim().toLowerCase()
	const filteredProducts = (products || []).filter((item) => {
		if (!normalizedSearch) return true
		const name = item?.article?.name || ''
		const description = item?.article?.description || ''
		const sku = item?.sku || ''
		return `${name} ${description} ${sku}`.toLowerCase().includes(normalizedSearch)
	})

  const visibleProducts = filteredProducts.slice(0, visibleCount)

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + 8) // mostrar 8 más
  }

  const handleClose = () => {
    onClose()
    setSearch('')
    setVisibleCount(8)
		setSelectedProducts([])
  }

	const toggleSelect = (product) => {
		setSelectedProducts((prev) => {
			const exists = prev.find(p => p.article_id === product.article_id);
			if (exists) {
				return prev.filter(p => p.article_id !== product.article_id);
			} else {
				return [...prev, product];
			}
		})
	};

	const getBestOffer = (applicableOffers, salePrice) => {
		if (!applicableOffers || applicableOffers.length === 0) return null
		const sortedOffers = [...applicableOffers].sort((a, b) => b.priority - a.priority)
		const bestOffer = sortedOffers[0]

		let finalPrice = salePrice || 0
		let discountAmount = 0

		if (bestOffer.type === 'percentage' && bestOffer.discount_percentage) {
			discountAmount = (finalPrice * bestOffer.discount_percentage) / 100
			finalPrice = finalPrice - discountAmount
		} else if (bestOffer.type === 'fixed_amount' && bestOffer.discount_amount) {
			discountAmount = bestOffer.discount_amount
			finalPrice = finalPrice - discountAmount
		}

		return {
			...bestOffer,
			final_price: Math.max(0, finalPrice),
			discount_amount: discountAmount,
		}
	}

  return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="lg"
			fullWidth
			PaperProps={{ sx: { borderRadius: 3 } }}
		>
			<DialogTitle sx={{ pb: 1 }}>
				<Typography variant="h6" fontWeight={700}>Productos</Typography>
				<Typography variant="body2" color="text.secondary">
					Seleccionados: {selectedProducts.length}
				</Typography>
        <IconButton
          aria-label="cerrar"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
					inputRef={searchInputRef}
          fullWidth
          placeholder="Buscar por nombre, descripción o SKU"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
					onKeyDown={(e) => {
				if (e.key === 'Enter') {
					e.preventDefault()
					focusSearchInput()
				}
			}}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />

        <Divider sx={{ mb: 2 }} />

        {/* Galería de productos */}
        <Grid container spacing={3}>
          {visibleProducts.map((article, idx) => {
						const { article: { name, description, images }, prices, quantity, article_id, sku, applicable_offers } = article;
						const isSelected = selectedProducts.some(p => p.article_id === article_id);
						const bestOffer = getBestOffer(applicable_offers, prices?.sale_price || 0);

            return <Grid item xs={12} sm={6} md={4} lg={3} key={idx} sx={{ display: 'flex' }}>
							<Box position="relative" width="100%">
								{isSelected && (
									<Chip
										icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
										label="Seleccionado"
										size="small"
										color="primary"
										sx={{ position: 'absolute', top: 8, left: 8, zIndex: 11 }}
									/>
								)}

								{/* Badge flotante de descuento */}
								{bestOffer && (
									<Box
										sx={{
											position: 'absolute',
											top: -8,
											right: -8,
											background: theme.palette.error.main,
											color: 'common.white',
											borderRadius: '50%',
											width: 50,
											height: 50,
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											justifyContent: 'center',
											fontWeight: 'bold',
											boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.4)}`,
											zIndex: 10
										}}
									>
										<Typography variant="caption" sx={{ fontSize: '0.9rem', lineHeight: 1, fontWeight: 'bold' }}>
											-{bestOffer.discount_percentage || Math.round((bestOffer.discount_amount / prices.sale_price) * 100)}%
										</Typography>
										<Typography variant="caption" sx={{ fontSize: '0.45rem', lineHeight: 1 }}>
											OFF
										</Typography>
									</Box>
								)}
								
								<Card 
									sx={{
										height: '100%',
										display: 'flex',
										flexDirection: 'column',
										border: isSelected ? `4px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
										boxShadow: isSelected ? 4 : 1,
										cursor: 'pointer',
										transition: 'all 0.2s ease',
										'&:hover': {
											boxShadow: 6,
											transform: 'translateY(-2px)'
										}
									}}
								>
									{/* Imagen del producto */}
									<CardMedia sx={{ height: 200, flexShrink: 0 }}>
										<Banner images={images} />
									</CardMedia>

									{/* Detalles */}
									<CardContent onClick={() => quantity && toggleSelect(article)} sx={{
										flexGrow: 1,
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'space-between',
										cursor: 'pointer',
										overflow: 'hidden',
										p: 2
									}}>
										<Box>
										<Typography 
											variant="subtitle1"
											fontWeight="bold"
											sx={{
												display: '-webkit-box',
												WebkitLineClamp: 1,
												WebkitBoxOrient: 'vertical',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												mt: 1
											}}
										>
											{name}
										</Typography>

										<Chip
											label={quantity > 0 ? `Stock: ${quantity}` : 'Sin stock'}
											size="small"
											color={quantity > 0 ? 'success' : 'default'}
											variant={quantity > 0 ? 'outlined' : 'filled'}
											sx={{ alignSelf: 'flex-start', mt: 1 }}
										/>
										
										{/* Precio con oferta */}
										{bestOffer ? (
											<Box display="flex" flexDirection="column" gap={0.5} my={1}>
												<Box display="flex" alignItems="center" gap={1}>
													<Typography
														variant="body2"
														sx={{
															textDecoration: 'line-through',
															color: 'text.secondary'
														}}
													>
															${(prices?.sale_price || 0).toLocaleString()}
													</Typography>
													<Chip
															label={`-${bestOffer.discount_percentage || Math.round((bestOffer.discount_amount / (prices?.sale_price || 1)) * 100)}%`}
														size="small"
														sx={{
															height: 18,
															fontSize: '0.65rem',
															fontWeight: 'bold',
															background: theme.palette.error.main,
															color: 'common.white'
														}}
													/>
												</Box>
												<Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>
													${bestOffer.final_price?.toLocaleString()}
												</Typography>
												<Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
													¡Ahorras ${bestOffer.discount_amount?.toLocaleString()}!
												</Typography>
											</Box>
										) : (
											<Typography variant="h6" color="success.main" my={1}>
												${(prices?.sale_price || 0).toLocaleString()}
											</Typography>
										)}
										
										{/* Ofertas aplicables */}
										{applicable_offers && applicable_offers.length > 0 && (
											<Box mt={1}>
												<Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
													{applicable_offers.slice(0, 2).map((offer) => (
														<Chip
															key={offer._id}
															label={offer.name}
															size="small"
															variant="outlined"
															color="secondary"
															icon={<LocalOfferIcon sx={{ fontSize: 12 }} />}
															sx={{ fontSize: '0.65rem', height: 20 }}
														/>
													))}
													{applicable_offers.length > 2 && (
														<Chip
															label={`+${applicable_offers.length - 2}`}
															size="small"
															variant="outlined"
															color="secondary"
															sx={{ fontSize: '0.65rem', height: 20 }}
														/>
													)}
												</Stack>
											</Box>
										)}
										
										{/* Variantes (Colores, Tallas y Cantidad) */}
										{article.type_detail && article.type_detail.length > 0 && (
											<Box mt={1}>
												<VariantsDisplay typeDetail={article.type_detail} compact />
											</Box>
										)}
										
										<Typography 
											variant="body2"
											color="text.secondary"
											mt={1}
											sx={{
												display: '-webkit-box',
												WebkitLineClamp: 2,
												WebkitBoxOrient: 'vertical',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												mt: 1
											}}
										>
											{description}
										</Typography>
										<Typography variant="body2" color="text.secondary" mt={1} fontWeight="bold">
											{sku}
										</Typography>
										</Box>
									</CardContent>
								</Card>
								{quantity === 0 && <div className="out-of-stock">SIN STOCK</div>}
							</Box>
            </Grid>
						})}
        </Grid>

				{filteredProducts.length === 0 && (
					<Box py={6} width="100%" textAlign="center">
						<Typography variant="body1" color="text.secondary">No se encontraron productos.</Typography>
					</Box>
				)}

        {/* Botón Ver más */}
        {visibleCount < filteredProducts.length && (
          <Box mt={4} textAlign="center">
            <Button variant="contained" onClick={handleShowMore}>
              Ver más productos
            </Button>
          </Box>
        )}
      </DialogContent>
			<DialogActions>
				<Box width="100%" display="flex" justifyContent="space-between" alignItems="center" px={1}>
					<Typography variant="body2" color="text.secondary">
						Seleccionados: {selectedProducts.length}
					</Typography>
					<Button
						onClick={() => {
							onSave && onSave(selectedProducts)
							focusSearchInput()
						}}
						color="primary"
						variant="contained"
						disabled={selectedProducts.length === 0}
					>
						Agregar seleccionados
					</Button>
				</Box>
			</DialogActions>
    </Dialog>
  )
}
